import { Calendar, Sparkles } from 'lucide-react'
import { statusLabel } from '../../data/activeIntake'

/**
 * IntakeBadge — small reusable pill that labels the active intake.
 * Used in the nav, hero, banners and section headers so the intake label
 * stays consistent everywhere it appears.
 *
 * Props:
 *   intake    — the active intake object (or null for evergreen mode)
 *   variant   — 'nav' | 'section' | 'inline'  (sizing/spacing tweaks)
 *   showPulse — animate the status dot (use in nav)
 */
export default function IntakeBadge({ intake, variant = 'inline', showPulse = false }) {
  if (!intake || intake.status === 'none') return null

  const label = intake.shortLabel || intake.navLabel || intake.name
  const status = statusLabel(intake.status)

  const sizeClass =
    variant === 'nav'
      ? 'text-[0.65rem] px-2 py-0.5 gap-1.5'
      : variant === 'section'
        ? 'text-xs px-3 py-1 gap-2'
        : 'text-xs px-3 py-1 gap-2'

  const tone =
    intake.status === 'closing-soon'
      ? 'border-cinema-amber/40 bg-cinema-amber/10 text-cinema-amber'
      : intake.status === 'upcoming'
        ? 'border-white/20 bg-white/5 text-cinema-soft'
        : intake.status === 'closed'
          ? 'border-white/10 bg-white/5 text-cinema-muted'
          : 'border-cinema-gold/30 bg-cinema-gold/10 text-cinema-gold'

  return (
    <span
      className={`inline-flex items-center rounded-full border uppercase tracking-[0.14em] font-sans font-semibold whitespace-nowrap select-none ${sizeClass} ${tone}`}
    >
      {showPulse && (intake.status === 'open' || intake.status === 'closing-soon') ? (
        <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cinema-gold opacity-60" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cinema-gold" />
        </span>
      ) : (
        <Calendar className={variant === 'nav' ? 'w-3 h-3' : 'w-3.5 h-3.5'} aria-hidden="true" />
      )}
      <span>{label}</span>
      {status && (
        <span className="opacity-70 font-medium normal-case tracking-normal">· {status}</span>
      )}
    </span>
  )
}

/**
 * Inline accent badge for hero eyebrows / section intros — uses Sparkles
 * instead of the status dot, for a more editorial feel.
 */
export function IntakeEyebrowBadge({ intake, icon: Icon = Sparkles }) {
  if (!intake || intake.status === 'none') return null
  const label = intake.shortLabel || intake.navLabel || intake.name
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-xs uppercase tracking-[0.16em] font-sans font-semibold">
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      <span>{label}</span>
    </span>
  )
}
